import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { PersonRow } from "@/lib/api";
import { displayName, LINKABLE_PROVIDERS, platformLabel } from "@/lib/people-levels";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useState } from "react";
import { initials } from "./person-format";
import { PlatformIcons } from "./PersonParts";

// "accounts": a dashboard person picks one or more unlinked chat accounts.
// "person": a chat account picks the one dashboard person it belongs to.
// `likely` names options the name/email match points at: sorted first,
// tagged, and selected when the dialog opens.
export interface LinkPicker {
  mode: "accounts" | "person";
  subject: PersonRow;
  options: PersonRow[];
  likely: ReadonlySet<string>;
}

export function LinkPickerDialog({
  picker,
  onClose,
  onPick,
}: {
  picker: LinkPicker | null;
  onClose: () => void;
  onPick: (chosen: PersonRow[]) => void;
}) {
  return (
    <Dialog open={picker !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">{picker && <PickerBody key={picker.subject.personId} picker={picker} onClose={onClose} onPick={onPick} />}</DialogContent>
    </Dialog>
  );
}

function PickerBody({ picker, onClose, onPick }: { picker: LinkPicker; onClose: () => void; onPick: (chosen: PersonRow[]) => void }) {
  const { mode, subject, likely } = picker;
  const options = [...picker.options].sort(
    (a, b) => Number(likely.has(b.personId)) - Number(likely.has(a.personId)) || displayName(a).localeCompare(displayName(b)),
  );
  const [picked, setPicked] = useState<string[]>(() => options.filter((o) => likely.has(o.personId)).map((o) => o.personId));
  const multi = mode === "accounts";
  const chosen = options.filter((o) => picked.includes(o.personId));
  const name = displayName(subject);
  const toggle = (id: string) =>
    setPicked((prev) => (multi ? (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]) : prev.includes(id) ? [] : [id]));

  return (
    <>
      <DialogHeader>
        <DialogTitle>{multi ? `Link a chat account to ${name}` : `Link ${name} to a dashboard person`}</DialogTitle>
        <DialogDescription>
          {multi
            ? "Chat accounts on this Boo that don't belong to anyone with a dashboard account yet. Linked accounts get the person's level."
            : `Pick who this ${accountLabel(subject)} belongs to. It will get that person's level.`}
        </DialogDescription>
      </DialogHeader>
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground text-pretty">
          {multi ? "Every chat account on this Boo is already linked to someone." : "Nobody has a dashboard account on this Boo yet. Invite someone or add them from your organization first."}
        </p>
      ) : (
        <Command className="rounded-lg border" filter={(value, query) => (value.toLowerCase().includes(query.trim().toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder={multi ? "Search chat accounts" : "Search by name or email"} />
          <CommandList className="max-h-72">
            <CommandEmpty>No one matches.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const selected = picked.includes(o.personId);
                const label = displayName(o);
                return (
                  <CommandItem
                    key={o.personId}
                    value={`${label} ${o.name} ${o.email ?? ""} ${o.handles.map((h) => `${platformLabel(h.provider)} ${h.workspace}`).join(" ")}`}
                    onSelect={() => toggle(o.personId)}
                    className="gap-3"
                  >
                    {multi ? (
                      <Checkbox checked={selected} tabIndex={-1} aria-label={`Link ${label}`} className="pointer-events-none" />
                    ) : (
                      <span className={cn("flex size-4 shrink-0 items-center justify-center", !selected && "opacity-0")} aria-hidden>
                        <Check className="size-4" />
                      </span>
                    )}
                    <Avatar className="size-7 shrink-0">
                      <AvatarFallback className="text-[10px]">{initials(o.name, o.email ?? "?")}</AvatarFallback>
                    </Avatar>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm">{label}</span>
                      <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                        {multi && <PlatformIcons handles={o.handles} />}
                        <span className="truncate">{multi ? accountDetail(o) : (o.email ?? "Dashboard")}</span>
                      </span>
                    </span>
                    {likely.has(o.personId) && (
                      <Badge variant="secondary" className="shrink-0 font-normal">
                        Likely match
                      </Badge>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={chosen.length === 0} onClick={() => onPick(chosen)}>
          {multi && chosen.length > 1 ? `Link ${chosen.length} accounts` : "Link"}
        </Button>
      </DialogFooter>
    </>
  );
}

function accountLabel(p: PersonRow): string {
  const h = p.handles.find((x) => LINKABLE_PROVIDERS.has(x.provider));
  return h ? `${platformLabel(h.provider)} account` : "chat account";
}

// "Slack · workspace T0123ABC456 · dave@acme.com": one line per account.
function accountDetail(p: PersonRow): string {
  const parts = p.handles
    .filter((h) => LINKABLE_PROVIDERS.has(h.provider))
    .map((h) => (h.workspace ? `${platformLabel(h.provider)} · workspace ${h.workspace}` : platformLabel(h.provider)));
  if (p.email) parts.push(p.email);
  return [...new Set(parts)].join(" · ");
}
